import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Form, Card, Button, Spin, message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import EOInsurance from './EOInsurance'
import TaxInformation from './TaxInformation'
import {
  updateCompany,
} from '@actions/currentUser'
import {
  selectAppraiserUpdateStatus,
  selectCurrentUserStatus,
  selectCurrentUserId,
  selectCurrentUserCompanyId,
  selectEOFileUrl,
  selectEOStatus,
} from '@reducers/currentUser/currentUserSelectors'
import { COMPANY_MEMBER } from '@components/Signup/constants'
import { openErrorNotification } from '../../helpers'
import moment from 'moment'
import { SUCCEEDED, ERROR } from '@constants/fetchStatuses'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import useSignupPageView from '@components/Signup/SegmentAnalytics/useSignupPageView'
import './LegalVerification.scss'
import { createTaxProfile, fetchTaxProfiles } from '@reducers/taxProfiles/taxProfilesSlice'
import {
  selectIsCreateTaxProfileLoading,
  selectCreateTaxProfileError,
  selectW9FileUrl,
  selectTaxProfilesData,
  selectTaxProfilesError,
  selectIsTaxProfilesSucceeded,
} from '@reducers/taxProfiles/taxProfilesSelectors'

const dateFormat = 'YYYY-MM-DD'

const LegalVerification = ({
  goToNextStep,
  goToPreviousStep,
  nextStepName,
  userType,
  stepName,
}) => {
  useSignupPageView('Account Setup Legal Verification')
  const track = useSignupAnalyticsTracker()
  // Pre-populate file fields if we have EO url
  const eoUrl = useSelector(selectEOFileUrl(userType !== COMPANY_MEMBER))
  const w9FileUrl = useSelector(selectW9FileUrl)
  const [previouslyUploadedEOFile, setPreviouslyUploadedEOFile] = useState(!!eoUrl)
  const [eoFile, setEOFile] = useState(null)
  const [uploadingEO, setUploadingEO] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const isCreateTaxProfileLoading = useSelector(selectIsCreateTaxProfileLoading)
  const createTaxProfileError = useSelector(selectCreateTaxProfileError)
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const currentUserID = useSelector(selectCurrentUserId)
  const eoStatus = useSelector(selectEOStatus)
  const appraiserUpdateStatus = useSelector(selectAppraiserUpdateStatus)
  const taxProfilesData = useSelector(selectTaxProfilesData)
  const taxProfilesError = useSelector(selectTaxProfilesError)
  const taxProfilesSucceeded = useSelector(selectIsTaxProfilesSucceeded)
  const isCompanyAdmin = userType !== COMPANY_MEMBER
  const [taxProfilesLoading, setTaxProfilesLoading] = useState(
    isCompanyAdmin && !(taxProfilesSucceeded || taxProfilesError)
  )

  const companyID = useSelector(selectCurrentUserCompanyId)
  const formRef = useRef()
  const dispatch = useDispatch()
  const isIndividual = userType === COMPANY_MEMBER

  useEffect(() => {
    if (isCompanyAdmin && companyID && !(taxProfilesSucceeded || taxProfilesError)) {
      dispatch(fetchTaxProfiles(companyID))
    }

    if (taxProfilesSucceeded || taxProfilesError) {
      setTaxProfilesLoading(false)
    }
  }, [companyID, taxProfilesSucceeded, taxProfilesError, isCompanyAdmin])

  useEffect(() => {
    if (previouslyUploadedEOFile) {
      setEOFile(new File([eoUrl], 'E&O Insurance.pdf', { type: 'application/pdf' }))
    }
  }, [previouslyUploadedEOFile, eoUrl])

  const handleSubmit = async () => {
    try {
      const formValues = await formRef.current.validateFields()
      // Throw custom error if we are currently uploading a file
      if (uploadingEO) { throw Error() }
      if (isIndividual) {
        submitUserEO()
      } else {
        submitCompanyEO(formValues)
      }
    } catch ({ errorFields = [] }) {
      if (uploadingEO) {
        formRef.current?.setFields([{
          name: ['eo_file'],
          errors: ['Upload must complete to continue.'],
        }])
      }
      if (errorFields.length) {
        const errorText = errorFields.length > 1 ? 'errors' : 'error'
        message.error({
          content: `${errorFields.length} ${errorText} found`,
          style: { marginTop: '70vh' },
        })
      }
    }
  }

  const submitCompanyEO = (formValues) => {
    const {
      tax_classification,
      llc_classification,
      eo_insurance_company,
      eo_prior_acts_date,
      eo_prior_acts_other,
      eo_expiration_date,
      eo_claim_limit,
      eo_aggregate_limit,
    } = formValues

    const trackingPayload = {
      tax_classification,
      llc_classification,
      eo_expiration_date,
      eo_claim_limit,
      eo_aggregate_limit,
    }
    track({ eventName: `Account Setup ${nextStepName} Button Clicked ${stepName}`, payload: trackingPayload })

    const parsedEOPriorActs = eo_prior_acts_date
      ? moment(eo_prior_acts_other).format(dateFormat).toString()
      : eo_prior_acts_other

    const updateCompanyArgs = {
      eo_insurance_company,
      eo_prior_acts: parsedEOPriorActs,
      eo_expiration_date: moment(eo_expiration_date).format(dateFormat),
      eo_claim_limit,
      eo_aggregate_limit,
    }

    if ((taxProfilesData || []).length === 0) {
      dispatch(createTaxProfile({
        tax_profile: {
          start_date: moment.utc().format(),
          end_date: null,
          legal_name: formValues.legal_name,
          w9_file_url: w9FileUrl,
          company_id: companyID,
          created_by: currentUserID,
        },
        tax_profile_sensitive_data: {
          legal_name: formValues.legal_name,
          disregarded_entity_name: formValues.disregarded_entity_name,
          business_address: {
            address: formValues.address,
            city: formValues.city,
            state: formValues.state,
            zip_code: formValues.zip_code,
          },
          tax_classification: formValues.tax_classification,
          ssn: formValues.ssn,
          ein: formValues.ein,
        },
      }))
    }
    dispatch(updateCompany(updateCompanyArgs, companyID))
    setSubmitting(true)
  }

  const submitUserEO = () => {
    track({ eventName: `Account Setup ${nextStepName} Button Clicked ${stepName}`, payload: { using_company_eo: !eoFile } })
    goToNextStep()
  }

  const handleSetUploadingEO = value => {
    setPreviouslyUploadedEOFile(false)
    setUploadingEO(value)
    if (!value) {
      formRef.current.setFields([{
        name: ['eo_file'],
        errors: [],
      }])
    }
  }

  // Block for user submit functionality
  useEffect(() => {
    if (!isIndividual || !submitting || isCreateTaxProfileLoading) { return }

    if (appraiserUpdateStatus === ERROR) {
      setSubmitting(false)
      openErrorNotification()
    } else if (appraiserUpdateStatus === SUCCEEDED) {
      goToNextStep()
    }
  }, [isIndividual, isCreateTaxProfileLoading, submitting, appraiserUpdateStatus, goToNextStep])

  // Block for company submit functionality
  useEffect(() => {
    if (isIndividual || !submitting || isCreateTaxProfileLoading) { return }

    if (currentUserStatus === SUCCEEDED &&
        (!eoStatus || eoStatus === SUCCEEDED)) {
      goToNextStep()
    } else if (currentUserStatus === ERROR || eoStatus === ERROR) {
      setSubmitting(false)
      openErrorNotification()
    }
  }, [
    isIndividual,
    isCreateTaxProfileLoading,
    submitting,
    currentUserStatus,
    eoStatus,
    goToNextStep,
  ])

  const clearFieldErrors = (changedValues) => {
    const updatedFields = Object.keys(changedValues)
      .filter((name) => formRef.current.getFieldError(name).length)
      .map((name) => ({ name, errors: [] }))
    formRef.current.setFields(updatedFields)
  }

  const handleBack = () => {
    track({ eventName: `Account Setup Back Button Clicked ${stepName}` })
    goToPreviousStep()
  }

  return (
    <Fragment>
      <Form
        className='legal-verification-form'
        ref={formRef}
        validateTrigger='onSubmit'
        onValuesChange={clearFieldErrors}
        data-testid="signup-legal-verification-step"
      >
        {isCompanyAdmin && (
          <TaxInformation loading={taxProfilesLoading} />
        )}
        <EOInsurance
          eoFile={eoFile}
          setEOFile={setEOFile}
          setUploadingEO={handleSetUploadingEO}
          currentUserID={currentUserID}
          companyID={companyID}
          uploadingEO={uploadingEO}
          setPreviouslyUploadedEOFile={setPreviouslyUploadedEOFile}
          userType={userType}
        />
      </Form>
      <Card style={{ marginTop: '5px' }}>
        {createTaxProfileError && (
          <div>
            <p>{createTaxProfileError}</p>
          </div>
        )}
        <div className='controls'>
          <Button name='back' type='default' onClick={handleBack} data-testid="signup-legal-verification-back-button">
            Back
          </Button>
          <Button
            type='primary'
            onClick={handleSubmit}
            name='continue'
            disabled={submitting || isCreateTaxProfileLoading || (isCompanyAdmin && taxProfilesLoading)}
            data-testid="signup-legal-verification-continue-button"
          >
            {submitting || isCreateTaxProfileLoading ? <Spin /> : nextStepName}
          </Button>
        </div>
      </Card>
    </Fragment>
  )
}

export default LegalVerification
